import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../api/client";

// The Admin's "Test as role" (owner, 2026-10-28; O-R165–O-R170). Unlike "View as role" (read-only, a signed header), the
// session lives on the server: nothing is sent from the browser, and every request is re-checked there. While it runs the
// Admin keeps their own permissions and also holds the tested role's, every action is real and signed by them, and the
// role's emails, Teams messages and notices also come to them marked "[Test as …]". This file only shows and controls it.

export interface TestAsRoleOption {
  role: string;
  label: string;
}

export interface TestAsStatus {
  active: boolean;
  role: string | null;
  roleLabel: string | null;
  startedAtUtc: string | null;
  endsAtUtc: string | null;
  /** True when this very request was authorized with the tested role (false while viewing as a role). */
  applied: boolean;
  canTestAs: boolean;
  roles: TestAsRoleOption[];
  maxHours: number;
  valueKind: string;
}

export interface TestAsSession {
  sessionId: string;
  upn: string;
  displayName: string;
  role: string;
  roleLabel: string;
  startedAtUtc: string;
  endsAtUtc: string;
  endedAtUtc: string | null;
  state: "active" | "stopped" | "expired";
  valueKind: string;
  stateValueKind: string;
}

export const TEST_AS_QK = {
  status: ["test-as", "status"] as const,
  sessions: ["test-as", "sessions"] as const
};

export const testAsApi = {
  status: async (): Promise<TestAsStatus> => (await apiClient.get<TestAsStatus>("/test-as")).data,
  start: async (role: string): Promise<TestAsStatus> => (await apiClient.post<TestAsStatus>("/test-as", { role })).data,
  stop: async (): Promise<{ stopped: boolean; role: string | null; roleLabel: string | null }> =>
    (await apiClient.post<{ stopped: boolean; role: string | null; roleLabel: string | null }>("/test-as/stop")).data,
  sessions: async (): Promise<TestAsSession[]> => (await apiClient.get<TestAsSession[]>("/admin/test-as/sessions")).data
};

/** This person's test session, read only for Admins (nobody else can have one), refreshed every minute. */
export function useTestAsStatus(enabled: boolean) {
  return useQuery({
    queryKey: TEST_AS_QK.status,
    queryFn: testAsApi.status,
    enabled,
    staleTime: 30_000,
    refetchInterval: enabled ? 60_000 : false,
    retry: false
  });
}

/** "15:40" in plant time (US Eastern), as the owner wrote it. */
export function endsAtText(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "America/New_York" });
}

/** The banner's words (owner's wording): "Testing as Purchaser — actions are real and signed by you — ends 15:40". */
export function testAsBannerText(roleLabel: string, endsAtUtc: string | null | undefined): string {
  const ends = endsAtText(endsAtUtc);
  return `Testing as ${roleLabel} — actions are real and signed by you${ends ? ` — ends ${ends}` : ""}`;
}

/** The subject prefix the tester's copies carry, as the server writes it. */
export function testAsPrefix(roleLabel: string): string {
  return `[Test as ${roleLabel}]`;
}

/** "Stop testing": ends the session on the server (audited), then reloads so menus and pages follow the real roles. */
export async function stopTestAs(reload = true): Promise<void> {
  try {
    await testAsApi.stop();
  } finally {
    if (reload) window.location.assign("/");
  }
}
